import { useLocalLogic } from "@local-logic/elements-react";
import { useQuery } from "@tanstack/react-query";
import { Scores } from "@local-logic/client";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo } from "react";
import { useStateManagerContext } from "~/context/stateManager";
import { schoolsKeys, schoolsResponse } from "~/features/schools";
import useDebounce from "~/hooks/useDebounce";
import { LocalContentProps } from "~/sdks/LocalContent/types";

export function useSchools({
  radius = 2000,
  activeScore
}: {
  radius?: number;
  activeScore?: keyof Scores["location"] | "";
}) {
  const flags = useFlags();
  const { client } = useLocalLogic();
  const { sdkState } = useStateManagerContext<LocalContentProps>();
  const debouncedOptions = useDebounce(sdkState.options!, 250);

  const isActive =
    activeScore === "high_schools" || activeScore === "primary_schools";

  const location = useMemo(() => {
    const hasMarker = !!debouncedOptions?.marker;

    if (hasMarker) {
      return debouncedOptions.marker;
    }

    return {
      lat: debouncedOptions?.lat,
      lng: debouncedOptions?.lng,
    };
  }, [debouncedOptions]);

  const options = {
    ...location,
    limit: 10,
    attemptMinimum: flags.lcMinimumPoi ? 3 : undefined,
    radius,
    include_catchments: true,
  };

  return useQuery({
    queryKey: schoolsKeys.list(options),
    queryFn: async () =>
      client.getSchools(options as unknown as schoolsResponse),
    enabled: !!options && !!options.lat && !!options.lng && isActive,
  });
}
